// The pedestrian body: the owner's OWN rigged character, worn over the existing cyborg brains.
//
// mesh-forge-ai (the drawings->rig pipeline) exports robot_ped.glb: a 40k-tri LOD of the
// reconstructed robot with the marker-driven skeleton and four baked clips. This module loads it
// once and hands out skinned clones. The Cyborg class keeps ALL of its behaviour - routes,
// reactions, stance height, the reach solver, the handshake contact loop - and this body simply
// follows the brain: position and heading from the brain's own numbers, the Walk clip rate-matched
// to the brain's gait clock, the head bones steered by the brain's glance state, and the RIGHT arm
// re-aimed at the handshake point so the visible hand is the hand the player's palm meets.
//
// The driven avatar (the player) deliberately keeps the procedural body: its crouch/reach IK is
// load-bearing for the certified FP interactions, and the player never sees their own torso.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

let _gltf = null;

/** Load once, before the population spawns. Resolves to a factory the Cyborg constructor calls. */
export async function loadRobotBody(url = './assets/robot_ped.glb') {
  _gltf = await new GLTFLoader().loadAsync(url);
  _gltf.scene.traverse((o) => {
    if (o.isMesh) { o.castShadow = true; o.frustumCulled = true; }
  });
  return (scene) => new RobotBody(scene);
}

const _q = new THREE.Quaternion();
const _qp = new THREE.Quaternion();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();

export class RobotBody {
  constructor(scene) {
    this.group = SkeletonUtils.clone(_gltf.scene);
    scene.add(this.group);
    this.mixer = new THREE.AnimationMixer(this.group);
    const walkClip = _gltf.animations.find((c) => c.name.toLowerCase().includes('walk'));
    this.walkDur = walkClip.duration;
    this.walk = this.mixer.clipAction(walkClip);
    this.walk.setLoop(THREE.LoopRepeat, Infinity).play();
    this.walkWeight = 1;

    this.bones = {};
    this.group.traverse((o) => { if (o.isBone) this.bones[o.name] = o; });
    // GLTFLoader sanitises the rig's dotted names: shoulder.r -> shoulderr, head.x -> headx.
    this.head = this.bones.headx || null;
    this.neck = this.bones.neckx || null;
    // The handshake arm is the ANATOMICAL right: the character faces +Z, so its right side is -X,
    // which is the pipeline's "_r" chain.
    this.ball = this.bones.c_arm_twist_offsetr || null;   // shoulder ball - aiming this swings the arm
    this.fore = this.bones.forearm_stretchr || null;
    this.hand = this.bones.handr || null;

    // The FP palm chases this marker. Parenting it to the VISIBLE hand closes the loop on the
    // body the player actually sees - the brain's reachErr then measures this hand, not a ghost.
    this.handTip = new THREE.Object3D();
    this.handTip.position.set(0, 0.09, 0.015);            // palm centre, a hand-length down the bone
    if (this.hand) this.hand.add(this.handTip);

    this._restPose = null;
  }

  /**
   * Called by the brain at the end of _animate, when every behavioural number is final.
   * Reads: pos, heading, mode, interact, speed, gait.stride, headYaw, head.rotation.x (nod),
   * _reach, _meet, world.groundAt.
   */
  update(dt, brain) {
    // --- root: the brain's own position, feet on the brain's own ground -------------------------
    const g = brain.world.groundAt(brain.pos.x, brain.pos.z, 0);
    this.group.position.set(brain.pos.x, g, brain.pos.z);
    this.group.rotation.y = brain.heading;

    // --- locomotion: Walk rate-matched to the brain's gait clock --------------------------------
    // The brain walks at speed/stride cycles per second (0.8 Hz by construction). The clip is one
    // full cycle over walkDur seconds, so timeScale = cyclesPerSecond * walkDur keeps feet and
    // ground in agreement - the same no-slip contract the procedural gait enforces.
    const moving = brain.mode === 'walk' && !brain.interact;
    const wantW = moving ? 1 : 0;
    this.walkWeight += (wantW - this.walkWeight) * Math.min(1, dt * 6);
    this.walk.setEffectiveWeight(this.walkWeight);
    this.walk.timeScale = moving ? (brain.speed / brain.gait.stride) * this.walkDur : 0;
    this.mixer.update(dt);

    // --- head: the brain's glance, split across neck and head bones -----------------------------
    // Applied AFTER the mixer so the clip provides the base pose and the glance rides on top.
    // A bone's local +Y runs along the bone (spine-up here), so a local Y twist is a world yaw.
    if (this.neck) this.neck.quaternion.multiply(_q.setFromAxisAngle(_v1.set(0, 1, 0), brain.headYaw * 0.35));
    if (this.head) {
      this.head.quaternion.multiply(_q.setFromAxisAngle(_v1.set(0, 1, 0), brain.headYaw * 0.65));
      // The interaction nod the brain writes onto its own head group.
      const nod = brain.head ? brain.head.rotation.x : 0;
      if (nod) this.head.quaternion.multiply(_q.setFromAxisAngle(_v1.set(1, 0, 0), nod));
    }

    // --- handshake: aim the visible arm at the meet point ---------------------------------------
    const k = brain._reach || 0;
    if (k > 0.001 && brain._meet && this.ball && this.fore) {
      this.group.updateMatrixWorld(true);
      this._aimBone(this.ball, brain._meet, k);
      // A straight aimed arm reads robotic even on a robot; give the forearm a fraction of the
      // remaining bend toward the target.
      this._aimBone(this.fore, brain._meet, k * 0.5);
    }
  }

  /** Swing `bone` so its child-chain direction points at worldTarget, blended by k. The same
   *  conjugation the pipeline's pose tools use: the delta is built in world space and mapped into
   *  the bone's local frame through the parent's world quaternion. */
  _aimBone(bone, worldTarget, k) {
    bone.updateWorldMatrix(true, false);
    const W = _v1.setFromMatrixPosition(bone.matrixWorld);
    const child = bone.children.find((c) => c.isBone);
    if (!child) return;
    child.updateWorldMatrix(true, false);
    const cur = _v2.setFromMatrixPosition(child.matrixWorld).sub(W).normalize();
    const want = _v3.copy(worldTarget).sub(W).normalize();
    _q.setFromUnitVectors(cur, want);
    if (k < 1) _q.slerp(new THREE.Quaternion(), 1 - k);   // identity-blend = partial swing
    bone.parent.getWorldQuaternion(_qp);
    const qpi = _qp.clone().invert();
    bone.quaternion.premultiply(qpi.multiply(_q).multiply(_qp));
    bone.updateMatrixWorld(true);
  }
}
